import React from "react"
import styled, { css } from "styled-components"
import Arrow from "../../images/arrow.inline.svg"
import { Slide } from "./slider-item.component"
import SliderItem from "./slider-item.component"
import { AppFunctionComponent, RenderRichText } from "../../types"
import { ScrollAnchor } from "./sections/custom/innovation-lab-form/innovation-lab.component"
import { SectionHeading } from "./sections/section.component"
import RichText from "../../resolvers/rich-text/rich-text.resolver"

const sliderLayoutBreakpoint = 440

const SliderWrapper = styled.div`
  padding: 0;
  overflow: hidden;
  position: relative;
`

const Slider = styled.div`
  margin: 0 auto;
  max-width: 1160px;
  width: calc(100% - 30px);

  @media (min-width: 768px) {
    width: calc(100% - 60px);
  }
`

const SliderTrack = styled.div<{ currentSlideIndex: number }>`
  width: 100%;
  display: flex;
  user-select: none;
  flex-direction: row;

  @keyframes scroll {
    0% {
      transform: translateX(-100%);
    }
    20% {
      transform: translateX(-250%);
    }
    40% {
      transform: translateX(-400%);
    }
    60% {
      transform: translateX(-550%);
    }
    80% {
      transform: translateX(-700%); // Account for gap between slides
    }
    100% {
      transform: translateX(-850%);
    }
  }

  animation: scroll 100s linear infinite;

  /* &:hover {
    animation-play-state: paused; // Optional: pause on hover
  } */
`

const SlideItem = styled(SliderItem)`
  cursor: grab;
  min-width: 190px;
  max-width: 300px;
  min-height: 90px;

  width: 228px;
  height: 100px;
  user-select: none;

  div {
    box-shadow: none !important;
    width: 100%;
    height: 100px;
    margin-bottom: 0 !important;
    img {
      object-fit: contain !important;

      width: 100%;
      height: 100px;
    }
  }

  &:not(:last-of-type) {
    margin-right: 15px;
  }

  @media (min-width: ${sliderLayoutBreakpoint}px) {
    /* min-width: calc(50% - 7.5px); */
  }

  @media (min-width: 768px) {
    /* min-width: calc(50% - 15px); */
    &:not(:last-of-type) {
      margin-right: 30px;
    }
  }

  @media (min-width: 1240px) {
    width: 228px;
    height: 100px;
    &:not(:last-of-type) {
      margin-right: 79px;
    }
  }
`

export const ArrowIcon = styled(Arrow)`
  height: 15px;
  user-selectable: none;
  @media (min-width: 960px) {
    height: 22px;
  }
`

export const ArrowContainer = styled.div<{ active: boolean }>`
  top: 50%;
  opacity: 1;
  width: 32px;
  height: 32px;
  display: flex;
  cursor: pointer;
  position: absolute;
  border-radius: 50%;
  align-items: center;
  flex-direction: row;
  pointer-events: all;
  visibility: visible;
  user-selectable: none;
  justify-content: center;
  transform: translateY(-50%);
  background-color: ${({ theme }) => theme.color.arrowBackgroundDark};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;

  @media (min-width: 960px) {
    width: 50px;
    height: 50px;
  }

  ${({ active }) =>
    !active &&
    css`
      opacity: 0;
      visibility: hidden;
    `};
`

export const ArrowPrev = styled(ArrowContainer)`
  left: 0;

  svg {
    transform: rotateZ(180deg);
  }
`

export const ArrowNext = styled(ArrowContainer)`
  right: 0;
`

export const ArrowsWrapper = styled.div`
  top: 0;
  left: 50%;
  display: flex;
  height: 245px;
  max-width: 1420px;
  position: absolute;
  flex-direction: row;
  pointer-events: none;
  width: calc(100vw - 60px);
  transform: translateX(-50%);
  justify-content: space-between;

  @media (min-width: ${sliderLayoutBreakpoint}px) {
    height: calc(50vw - 30px);
  }

  @media (min-width: 768px) {
    width: calc(100vw - 90px);
    height: calc(50vw - 52.5px);
  }

  @media (min-width: 960px) {
    width: calc(100vw - 120px);
  }

  @media (min-width: 1240px) {
    height: 560px;
    width: 1100px;
  }

  @media (min-width: 1430px) {
    width: calc(100vw - 40px);
  }
`

const StyledSliderHeading = styled(SectionHeading)`
  margin-bottom: 48px;
`

interface Props {
  slides?: Record<string, any>[]
  className?: string
  anchorId?: string
  text?: RenderRichText
}

const SliderCarousel: AppFunctionComponent<Props> = ({
  className,
  slides = [],
  anchorId,
  text,
}) => {
  return (
    <SliderWrapper className={className}>
      <ScrollAnchor id={anchorId} />
      {text && (
        <StyledSliderHeading>
          <RichText content={text} />
        </StyledSliderHeading>
      )}
      <Slider>
        <SliderTrack currentSlideIndex={0}>
          {slides.map((slideItem: Slide, index: number) => (
            <SlideItem slide={slideItem} key={`original-${index}`} />
          ))}
          {slides.map((slideItem: Slide, index: number) => (
            <SlideItem slide={slideItem} key={`duplicate-1-${index}`} />
          ))}
          {slides.map((slideItem: Slide, index: number) => (
            <SlideItem slide={slideItem} key={`duplicate-2-${index}`} />
          ))}
          {slides.map((slideItem: Slide, index: number) => (
            <SlideItem slide={slideItem} key={`duplicate-3-${index}`} />
          ))}
        </SliderTrack>
      </Slider>
    </SliderWrapper>
  )
}

export default SliderCarousel
